import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "./authSlice";
import { auth, storage, db } from "../../firebase/firebaseConfig";
import { updateProfile as firebaseUpdateProfile } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { updateDriveBranchName } from "../../utils/branchNameResolver";

const MobileEditAccount = ({ isOpen, onClose, initialDetails, onSave, showAvatar = true, disabledFields = [], isAgentEdit = false }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Responsive breakpoints
  const isMobile = true;
  const isTablet = useMediaQuery('(max-width:1024px)');

  // Reusable TextField styling
  const textFieldStyles = {
    "& .MuiInputBase-root": {
      height: "44px",
      borderRadius: "8px",
      borderWidth: "1px",
      fontSize: "14px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#E0E0E0",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#BDBDBD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0063F7",
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      padding: "10px 14px",
      height: "22px",
      display: "flex",
      alignItems: "center",
      textAlign: "left",
      color: "#212121",
      fontFamily: "Roboto",
      fontWeight: 400,
      fontSize: "14px",
      "&::placeholder": {
        color: "#9E9E9E",
        opacity: 1,
        fontSize: "14px",
        textAlign: "left",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      color: "#757575",
      textAlign: "left",
      "&.Mui-focused": {
        fontSize: "14px",
        color: "#0063F7",
      },
    },
  };

  const [editMode, setEditMode] = useState(false);

  // Reset edit mode when modal opens - always start in view mode
  useEffect(() => {
    if (isOpen) {
      setEditMode(false);
    }
  }, [isOpen]);
  const [formData, setFormData] = useState(() => ({
    email: initialDetails?.email || "",
    name: initialDetails?.name || "",
    phone: initialDetails?.phone || initialDetails?.mobile || "",
    location: initialDetails?.location || initialDetails?.address || "",
    ...(!isAgentEdit && { referredBy: initialDetails?.referredBy || "" }),
  }));

  // Update form data when modal opens - fetch data based on context
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) return;

      // If initialDetails are provided (client editing), use them
      if (initialDetails && !isAgentEdit) {
        setFormData({
          email: initialDetails.email || "",
          name: (initialDetails.name || "").toUpperCase(),
          phone: initialDetails.phone || initialDetails.mobile || "",
          location: initialDetails.location || initialDetails.address || "",
          referredBy: initialDetails.referredBy || "",
        });
        return;
      }

      // For agent editing, fetch from agent-details collection
      if (isAgentEdit && user?.uid) {
        try {
          const currentUser = auth.currentUser;
          const docRef = doc(db, "agent-details", user.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const agentData = docSnap.data();
            setFormData({
              email: currentUser?.email || agentData.email || "",
              name: agentData.name || currentUser?.displayName || "",
              phone: agentData.phone || "",
              location: agentData.address || "",
            });
          } else {
            // Fallback to current user data if no agent-details found
            setFormData({
              email: currentUser?.email || "",
              name: currentUser?.displayName || "",
              phone: "",
              location: "",
            });
          }
        } catch (error) {
          console.error("Error fetching agent details:", error);
          // Fallback to current user data on error
          const currentUser = auth.currentUser;
          setFormData({
            email: currentUser?.email || "",
            name: currentUser?.displayName || "",
            phone: "",
            location: "",
          });
        }
      }
    };

    fetchData();
  }, [isOpen, user?.uid, initialDetails, isAgentEdit]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return ""; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const validatePhone = (phone) => {
    if (!phone) return ""; // Phone is optional
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phone)) return "Please enter a valid 10-digit phone number";
    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    newErrors.email = validateEmail(formData.email);

    // Skip phone validation if the field is disabled or it's an agent edit
    // This allows admin users with international numbers (e.g. +91) to save profile changes
    const phoneDisabled = disabledFields.includes("phone") || isAgentEdit;
    if (!phoneDisabled) {
      newErrors.phone = validatePhone(formData.phone);
    } else {
      newErrors.phone = "";
    }

    setErrors(newErrors);

    // Return true if no errors
    return !Object.values(newErrors).some(error => error !== "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply input restrictions based on field type
    if (name === 'phone') {
      // Only allow digits for mobile, limit to 10 characters
      processedValue = value.replace(/[^0-9]/g, '').substring(0, 10);
    }
    if (name === 'name' && !isAgentEdit) {
      processedValue = value.toUpperCase();
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };


  const handleSave = async () => {
    try {
      // Validate form before saving
      if (!validateForm()) {
        toast.error("Please fix the validation errors before saving", {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          style: {
            backgroundColor: '#FEE2E2',
            color: '#DC2626',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            padding: '12px 16px',
            border: '1px solid #FECACA',
          },
        });
        return;
      }

      if (onSave) {
        // For agent editing, exclude email field; for client editing, include it
        const dataToSave = isAgentEdit
          ? (() => {
            // Create a clean object with only the fields we want for agents
            const cleanData = {
              name: formData.name,
              phone: formData.phone,
              location: formData.location,
              photoURL: formData.photoURL
            };
            // Filter out any undefined values to prevent Firebase errors
            const filteredData = Object.fromEntries(
              Object.entries(cleanData).filter(([_, value]) => value !== undefined)
            );
            return filteredData;
          })()
          : formData;
        // Call the parent's save handler with the updated form data
        await onSave(dataToSave);
        // Don't show toast here as the parent component will handle it
      } else {
        // Fallback for agent profile updates - save to Firestore
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Update displayName if name was changed
          if (formData.name && formData.name !== currentUser.displayName) {
            await firebaseUpdateProfile(currentUser, { displayName: formData.name });
          }

          // Update agent profile in Firestore
          const agentRef = doc(db, `agent-details`, currentUser.uid);
          const agentPayload = {
            name: formData.name || user.displayName || "",
            phone: formData.phone || user.phone || "",
            address: formData.location || user.address || "",
            email: currentUser.email || "",
            updatedAt: new Date().toISOString()
          };

          // Filter out any undefined values
          const cleanPayload = Object.fromEntries(
            Object.entries(agentPayload).filter(([_, value]) => value !== undefined)
          );

          await setDoc(agentRef, cleanPayload, { merge: true });

          // Update Drive branch name if location was changed (for staff users)
          // This ensures future uploads use the new branch name
          if (formData.location && !user?.isPhoneUser) {
            await updateDriveBranchName(currentUser.uid, formData.location);
            console.log('📂 Drive branch name updated to:', formData.location);
          }

          // Update Redux store with new user data
          dispatch(setUser({
            ...user,
            displayName: formData.name || user.displayName,
            phone: formData.phone || user.phone,
            address: formData.location || user.address,
          }));
        }

        setEditMode(false);
        onClose();
        // Add a small delay to ensure the modal is closed before showing toast
        setTimeout(() => {
          console.log("🎉 Agent profile update success - showing toast");
          toast.success("Agent profile successfully updated", {
            containerId: "bottom-center",
            position: "bottom-center",
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            closeButton: false,
            style: {
              backgroundColor: '#166434',
              color: '#FFFFFF',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              padding: '12px 16px',
              minHeight: '44px',
              alignItems: 'center',
              gap: '2px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              marginBottom: '75vh',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: 'auto',
              maxWidth: 'none',
              display: 'inline-flex',
            },
            icon: (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ color: '#FFFFFF' }}
              >
                <path
                  d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ),
          });
        }, 100);
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        style: {
          backgroundColor: '#FEE2E2',
          color: '#DC2626',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 500,
          padding: '12px 16px',
          border: '1px solid #FECACA',
        },
      });
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    try {
      setUploading(true);
      const storageRef = ref(storage, `avatars/${user.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);

      await firebaseUpdateProfile(auth.currentUser, { photoURL });
      dispatch(setUser({ ...user, photoURL }));
      console.log("🎉 Profile picture update success - showing toast");
      toast.success("Profile picture successfully updated", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#166434',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    } catch (err) {
      console.error("Image upload failed", err);
      toast.error("Failed to update picture", {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        style: {
          backgroundColor: '#FEE2E2',
          color: '#DC2626',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 500,
          padding: '12px 16px',
          border: '1px solid #FECACA',
        },
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={false}
      maxWidth="xs"
      PaperProps={{
        sx: {
          width: "90vw",
          maxWidth: "400px",
          borderRadius: "12px",
          padding: "16px",
          margin: "16px",
          maxHeight: "85vh",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontFamily: "Roboto",
          fontWeight: "600",
          fontSize: "18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 0 8px 0",
          minHeight: "auto",
        }}
      >
        {editMode ? "Edit Profile" : "View Profile"}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Avatar */}
      {showAvatar && (
        <Box display="flex" justifyContent="center" mb={1} position="relative">
          <Avatar src={user?.photoURL} sx={{ width: 80, height: 80 }} />
          {editMode && (
            <label
              htmlFor="avatar-upload"
              style={{
                position: "absolute",
                bottom: 0,
                right: "calc(50% - 50px)",
                cursor: "pointer",
                background: "#0063F7",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                border: "2px solid white",
              }}
            >
              📷
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </label>
          )}
        </Box>
      )}

      {/* Form Fields */}
      <DialogContent sx={{ padding: "8px 0", overflow: "auto", flex: 1 }}>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <TextField
            name="phone"
            label="Mobile"
            value={formData.phone}
            onChange={handleChange}
            disabled={!editMode || disabledFields.includes("phone") || (isAgentEdit && user?.isPhoneUser)}
            error={!!errors.phone}
            helperText={errors.phone}
            fullWidth
            size="small"
            sx={{
              "& .MuiInputBase-root": {
                height: "40px",
                fontSize: "14px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
                "&.MuiInputLabel-shrink": {
                  fontSize: "12px",
                  transform: "translate(14px, -9px) scale(0.75)",
                }
              },
              "& .MuiOutlinedInput-notchedOutline": {
                "& legend": {
                  fontSize: "9px",
                }
              }
            }}
          />
          <TextField
            name="name"
            label="Name"
            value={formData.name}
            onChange={handleChange}
            disabled={!editMode || disabledFields.includes("name")}
            fullWidth
            size="small"
            sx={{
              "& .MuiInputBase-root": {
                height: "40px",
                fontSize: "14px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
                "&.MuiInputLabel-shrink": {
                  fontSize: "12px",
                  transform: "translate(14px, -9px) scale(0.75)",
                }
              },
              "& .MuiOutlinedInput-notchedOutline": {
                "& legend": {
                  fontSize: "9px",
                }
              }
            }}
          />
          {(!isAgentEdit || formData.email) && (
            <>
              <TextField
                name="email"
                label="E-mail"
                value={formData.email}
                onChange={handleChange}
                disabled={!editMode || disabledFields.includes("email") || isAgentEdit}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    height: "40px",
                    fontSize: "14px",
                    ...(isAgentEdit && {
                      backgroundColor: "#F5F5F5",
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                      },
                    }),
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                    "&.MuiInputLabel-shrink": {
                      fontSize: "12px",
                      transform: "translate(14px, -9px) scale(0.75)",
                    },
                    ...(isAgentEdit && {
                      color: "#9E9E9E",
                    }),
                  },
                  "& .MuiInputBase-input": {
                    ...(isAgentEdit && {
                      color: "#757575",
                      cursor: "not-allowed",
                    }),
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    "& legend": {
                      fontSize: "9px",
                    }
                  }
                }}
              />
              {isAgentEdit && (
                <Box sx={{
                  fontSize: "11px",
                  color: "#9E9E9E",
                  mt: -0.5,
                  fontStyle: "italic"
                }}>
                  Email cannot be changed as it's used for login credentials
                </Box>
              )}
            </>
          )}
          <TextField
            name="location"
            label={(isAgentEdit && !user?.isPhoneUser) ? "Branch Name" : "Address"}
            value={formData.location}
            onChange={handleChange}
            disabled={!editMode || disabledFields.includes("location")}
            fullWidth
            size="small"
            sx={{
              "& .MuiInputBase-root": {
                height: "40px",
                fontSize: "14px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
                "&.MuiInputLabel-shrink": {
                  fontSize: "12px",
                  transform: "translate(14px, -9px) scale(0.75)",
                }
              },
              "& .MuiOutlinedInput-notchedOutline": {
                "& legend": {
                  fontSize: "9px",
                }
              }
            }}
          />
          {!isAgentEdit && (
            <TextField
              name="referredBy"
              label="Referred by"
              value={formData.referredBy}
              onChange={handleChange}
              disabled={!editMode || disabledFields.includes("referredBy")}
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  height: "40px",
                  fontSize: "14px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "14px",
                  "&.MuiInputLabel-shrink": {
                    fontSize: "12px",
                    transform: "translate(14px, -9px) scale(0.75)",
                  }
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  "& legend": {
                    fontSize: "9px",
                  }
                }
              }}
            />
          )}
        </Box>
      </DialogContent>

      {/* Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={1.5} mt={2} mb={0}>
        {!editMode ? (
          <>
            <Button
              variant="outlined"
              sx={{
                flex: 1,
                height: "36px",
                borderRadius: "6px",
                fontSize: "14px",
                borderColor: "#B00020",
                color: "#B00020",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "inherit",
                  color: "#B00020",
                  boxShadow: 2
                }
              }}
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="contained"
              sx={{
                flex: 1,
                height: "36px",
                borderRadius: "6px",
                fontSize: "14px",
                backgroundColor: "#0063F7 !important",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#0051d4 !important",
                },
              }}
              onClick={() => setEditMode(true)}
            >
              Edit
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              sx={{
                flex: 1,
                height: "36px",
                borderRadius: "6px",
                fontSize: "14px",
                borderColor: "#B00020",
                color: "#B00020",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "inherit",
                  color: "#B00020",
                  boxShadow: 2
                }
              }}
              onClick={() => setEditMode(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                flex: 1,
                height: "36px",
                borderRadius: "6px",
                fontSize: "14px",
                backgroundColor: "#0063F7 !important",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#0051d4 !important",
                },
              }}
              onClick={handleSave}
              disabled={uploading}
            >
              {uploading ? "..." : "Save"}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default MobileEditAccount;
